import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeftIcon,
  BriefcaseIcon,
  UserGroupIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { getVacanciesList, getVacancyResponses, evaluateResume } from '../services/api';
import VacancyResponses from '../VacancyResponses';
import VacancyResponsesInline from '../VacancyResponsesInline';
import { ManagementPagination } from '../../components/ui/ManagementPagination';

interface VacanciesCardProps {
  onBackToSearch: () => void;
}

interface Vacancy {
  id: string;
  name: string;
  department?: {
    name: string;
  };
  area: string | { id: string; name: string; url?: string };
  salary?: {
    from?: number;
    to?: number;
    currency?: string;
    gross?: boolean;
  };
  responses_count?: number;
  archived?: boolean;
  premium?: boolean;
  published_at: string;
  alternate_url?: string;
}

const VacanciesCard: React.FC<VacanciesCardProps> = ({
  onBackToSearch
}) => {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const colors = useBrandingColors();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFound, setTotalFound] = useState(0);
  const [totalResponses, setTotalResponses] = useState(0);
  const [responsesVacancyId, setResponsesVacancyId] = useState<string | null>(null);
  const [expandedVacancyId, setExpandedVacancyId] = useState<string | null>(null);
  const [vacancyResponses, setVacancyResponses] = useState<Record<string, {
    items: any[];
    page: number;
    pages: number;
    found: number;
    per_page: number;
  }>>({});
  const [loadingResponses, setLoadingResponses] = useState<Record<string, boolean>>({});
  const [aiEvaluations, setAiEvaluations] = useState<Record<string, any>>({});
  const [evaluatingIds, setEvaluatingIds] = useState(new Set<string>());

  // Queue management for AI evaluations
  const [evaluationQueue, setEvaluationQueue] = useState<Array<{vacancyId: string, responseId: string, resumeData: any}>>([]);
  const [activeEvaluations, setActiveEvaluations] = useState(0);
  const [queuedEvaluations, setQueuedEvaluations] = useState<Record<string, boolean>>({});
  const maxConcurrentEvaluations = 3;

  const fetchVacancies = useCallback(async () => {
    try {
      setLoading(true);
      // Используем прямой API для получения вакансий как в оригинале
      const response = await getVacanciesList(currentPage, 20);
      
      setVacancies(response.items || []);
      setTotalPages(response.pages || 1);
      setTotalFound(response.found || 0);
      if (response.total_responses !== undefined) {
        setTotalResponses(response.total_responses);
      } else {
        setTotalResponses(response.items?.reduce((sum: number, v: Vacancy) => sum + (v.responses_count || 0), 0) || 0);
      }
    } catch (error) {
      console.error('Error fetching vacancies:', error);
      toast.error('Ошибка при загрузке вакансий');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchVacancies();
  }, [fetchVacancies]);

  // Process evaluation queue
  useEffect(() => {
    if (evaluationQueue.length > 0 && activeEvaluations < maxConcurrentEvaluations) {
      const itemsToProcess = evaluationQueue.slice(0, maxConcurrentEvaluations - activeEvaluations);

      itemsToProcess.forEach(item => {
        const evaluationKey = `${item.vacancyId}_${item.responseId}`;

        // Skip if already evaluating or evaluated
        if (!evaluatingIds.has(evaluationKey) && !aiEvaluations[evaluationKey]) {
          evaluateResumeForItem(item.vacancyId, item.responseId, item.resumeData);
        }
      });

      // Remove processed items from queue
      setEvaluationQueue(prev => prev.slice(itemsToProcess.length));
    }
  }, [evaluationQueue, activeEvaluations, evaluatingIds, aiEvaluations]);

  const toggleVacancyExpansion = async (vacancyId: string) => {
    const isExpanded = expandedVacancyId === vacancyId;

    if (!isExpanded) {
      setExpandedVacancyId(vacancyId);

      const meta = vacancyResponses[vacancyId];
      if (meta?.found !== undefined) {
        updateVacancyResponsesCount(vacancyId, meta.found);
      }
      await fetchVacancyResponsesData(vacancyId, meta?.page || 1, meta?.per_page || 20);
    } else {
      setExpandedVacancyId(null);

      // Clear queue items for this vacancy
      setEvaluationQueue(prev => prev.filter(item => item.vacancyId !== vacancyId));

      // Clear queued status for this vacancy
      setQueuedEvaluations(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(key => {
          if (key.startsWith(`${vacancyId}_`)) {
            delete newState[key];
          }
        });
        return newState;
      });
    }
  };

  const updateVacancyResponsesCount = useCallback((vacancyId: string, newCount: number) => {
    setVacancies(prev => {
      const updated = prev.map(v => {
        if (v.id === vacancyId) {
          const oldCount = v.responses_count || 0;
          const diff = newCount - oldCount;
          setTotalResponses(prevTotal => prevTotal + diff);
          return { ...v, responses_count: newCount };
        }
        return v;
      });
      return updated;
    });
  }, []);

  const fetchVacancyResponsesData = async (vacancyId: string, page: number, perPage: number) => {
    setLoadingResponses(prev => ({ ...prev, [vacancyId]: true }));

    try {
      const response = await getVacancyResponses(vacancyId, page, perPage);

      const items = response.responses?.items || [];
      const found = typeof response.responses?.found === 'number' ? response.responses.found : items.length;
      const pagesFromApi = typeof response.responses?.pages === 'number' ? response.responses.pages : Math.ceil(found / perPage);
      const pages = Math.max(1, pagesFromApi || 1);
      const safePage = Math.min(Math.max(1, page), pages);

      if (safePage !== page) {
        await fetchVacancyResponsesData(vacancyId, safePage, perPage);
        return;
      }

      updateVacancyResponsesCount(vacancyId, found);

      setVacancyResponses(prev => ({
        ...prev,
        [vacancyId]: {
          items,
          page: safePage,
          pages,
          found,
          per_page: perPage
        }
      }));

      // Add responses to evaluation queue
      const responsesToEvaluate = items
        .filter((item: any) => item.resume && !aiEvaluations[`${vacancyId}_${item.id}`])
        .map((item: any) => ({
          vacancyId,
          responseId: item.id,
          resumeData: item.resume
        }));

      if (responsesToEvaluate.length > 0) {
        // Mark all items as queued
        const queuedState: Record<string, boolean> = {};
        responsesToEvaluate.forEach((item: any) => {
          queuedState[`${item.vacancyId}_${item.responseId}`] = true;
        });
        setQueuedEvaluations(prev => ({ ...prev, ...queuedState }));

        // Add to queue
        setEvaluationQueue(prev => [...prev, ...responsesToEvaluate]);
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Ошибка при загрузке откликов');
      setVacancyResponses(prev => ({
        ...prev,
        [vacancyId]: {
          items: [],
          page: 1,
          pages: 1,
          found: 0,
          per_page: perPage
        }
      }));
    } finally {
      setLoadingResponses(prev => ({ ...prev, [vacancyId]: false }));
    }
  };

  const evaluateResumeForItem = async (vacancyId: string, responseId: string, resumeData: any) => {
    const evaluationKey = `${vacancyId}_${responseId}`;

    if (evaluatingIds.has(evaluationKey) || aiEvaluations[evaluationKey]) {
      return;
    }

    // Remove from queued and mark as evaluating
    setQueuedEvaluations(prev => {
      const newState = { ...prev };
      delete newState[evaluationKey];
      return newState;
    });

    setEvaluatingIds(prev => new Set(prev).add(evaluationKey));
    setActiveEvaluations(prev => prev + 1);

    try {
      const response = await evaluateResume(vacancyId, resumeData);

      setAiEvaluations(prev => ({
        ...prev,
        [evaluationKey]: response
      }));
    } catch (error) {
      console.error('Error evaluating resume:', error);
      setAiEvaluations(prev => ({
        ...prev,
        [evaluationKey]: { rating: 0, comment: 'Ошибка оценки' }
      }));
    } finally {
      setEvaluatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(evaluationKey);
        return newSet;
      });
      setActiveEvaluations(prev => prev - 1);
    }
  };

  const formatSalary = (salary?: any) => {
    if (!salary) return 'Не указана';
    
    let result = '';
    if (salary.from) result += `от ${salary.from.toLocaleString('ru-RU')}`;
    if (salary.to) result += `${result ? ' ' : ''}до ${salary.to.toLocaleString('ru-RU')}`;
    if (salary.currency) result += ` ${salary.currency}`;
    if (salary.gross === false) result += ' на руки';
    
    return result || 'Не указана';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusColor = (vacancy: Vacancy) => {
    if (vacancy.archived) return 'bg-gray-100 text-gray-800';
    if (vacancy.premium) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (vacancy: Vacancy) => {
    if (vacancy.archived) return 'В архиве';
    if (vacancy.premium) return 'Премиум';
    return 'Активна';
  };

  if (loading && vacancies.length === 0) {
    return (
      <div className="bg-[#f5f6f1] rounded-lg px-[10px] min-h-[calc(100vh-280px)] w-full">
        <div className="flex items-center justify-center h-64">
          <div className="inline-flex items-center">
            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Загрузка вакансий...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f6f1] rounded-lg px-[10px] min-h-[calc(100vh-280px)] w-full">
      {/* Statistics Summary */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <BriefcaseIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <div className="text-sm text-gray-500">Всего вакансий</div>
                <div className="text-2xl font-bold text-gray-900">{totalFound}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <div className="text-sm text-gray-500">Всего откликов</div>
                <div className="text-2xl font-bold text-gray-900">
                  {totalResponses}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-indigo-500 mr-3" />
              <div>
                <div className="text-sm text-gray-500">Активные</div>
                <div className="text-2xl font-bold text-gray-900">
                  {vacancies.filter(v => !v.archived).length}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <div className="text-sm text-gray-500">В архиве</div>
                <div className="text-2xl font-bold text-gray-900">
                  {vacancies.filter(v => v.archived).length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vacancies List */}
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-w-full">
          <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed', minWidth: '800px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3" style={{ width: '40px' }}></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>
                  Вакансия
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                  Локация
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                  Зарплата
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Отклики
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                  Опубликована
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vacancies.map((vacancy) => (
                <React.Fragment key={vacancy.id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleVacancyExpansion(vacancy.id)}
                >
                  <td className="px-3 py-4" style={{ width: '40px' }}>
                    {expandedVacancyId === vacancy.id ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4" style={{ width: '25%' }}>
                    <div className="truncate">
                      <div className="text-sm font-medium text-gray-900 truncate" title={vacancy.name}>
                        {vacancy.name}
                      </div>
                      {vacancy.department && (
                        <div className="text-sm text-gray-500 truncate" title={vacancy.department.name}>
                          {vacancy.department.name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '15%' }}>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      {typeof vacancy.area === 'string' ? vacancy.area : vacancy.area?.name || 'Не указана'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '15%' }}>
                    <div className="flex items-center text-sm text-gray-900">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {formatSalary(vacancy.salary)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center" style={{ width: '10%' }}>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {vacancy.responses_count || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '10%' }}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vacancy)}`}>
                      {getStatusText(vacancy)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" style={{ width: '15%' }}>
                    {formatDate(vacancy.published_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" style={{ width: '10%' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setResponsesVacancyId(String(vacancy.id));
                      }}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      title="Посмотреть отклики в модалке"
                    >
                      <svg className="h-5 w-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    {vacancy.alternate_url && (
                      <a
                        href={vacancy.alternate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-900"
                        title="Открыть на HH.ru"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="h-5 w-5 inline" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                    )}
                  </td>
                </tr>
                {/* Раскрывающаяся строка с откликами */}
                {expandedVacancyId === vacancy.id && (
                  <tr>
                    <td colSpan={8} className="px-0 py-0">
                      <div className="bg-gray-50 px-6 py-4">
                        {loadingResponses[vacancy.id] ? (
                          <div className="flex items-center justify-center py-4">
                            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Загрузка откликов...
                          </div>
                        ) : (
                          <VacancyResponsesInline
                            vacancyId={vacancy.id}
                            responses={vacancyResponses[vacancy.id]?.items || []}
                            aiEvaluations={aiEvaluations}
                            evaluatingIds={evaluatingIds}
                            queuedEvaluations={queuedEvaluations}
                            onRefresh={async () => {
                              const meta = vacancyResponses[vacancy.id];
                              await fetchVacancyResponsesData(vacancy.id, meta?.page || 1, meta?.per_page || 20);
                            }}
                            onRefreshVacancies={async () => {
                              await fetchVacancies();
                            }}
                          />
                        )}
                        {!!vacancyResponses[vacancy.id] && (vacancyResponses[vacancy.id]?.pages || 1) > 1 && (
                          <div className="mt-4">
                            <ManagementPagination
                              currentPage={vacancyResponses[vacancy.id].page}
                              totalPages={vacancyResponses[vacancy.id].pages}
                              totalItems={vacancyResponses[vacancy.id].found}
                              itemsPerPage={vacancyResponses[vacancy.id].per_page}
                              onPageChange={(p) => fetchVacancyResponsesData(vacancy.id, p, vacancyResponses[vacancy.id].per_page)}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Назад
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Вперед
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Страница <span className="font-medium">{currentPage}</span> из{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ArrowLeftIcon className="h-5 w-5 rotate-180" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Модалка откликов */}
      {responsesVacancyId && (
        <VacancyResponses
          vacancyId={responsesVacancyId}
          onClose={() => setResponsesVacancyId(null)}
        />
      )}
    </div>
  );
};

export default VacanciesCard;